import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiX, FiLayout, FiRefreshCcw, FiLink, FiZap, FiUpload, FiDownload, FiClipboard, FiCommand, FiType, FiBarChart2, FiLayers, FiWifi, FiWifiOff, FiTrash2, FiSun, FiMoon } = FiIcons;
import SafeIcon from '../common/SafeIcon';
import { APPS_SCRIPT_URL_KEY, TOKEN_VALUES_KEY } from '../utils/constants';

export default function SettingsModal({
  onClose,
  config,
  setConfig,
  appsScriptUrl,
  setAppsScriptUrl,
  onSyncNow,
  syncStatus,
  dirtyCount = 0,
  lastSyncAt = 0,
  isOnline = true,
  retryCount = 0,
  lastSyncError = '',
  blockedCount = 0,
  onExport,
  onImport,
  onInsights,
  onDuplicates,
  onTrash,
  theme = 'dark',
  onToggleTheme
}) {
  const PRESET_URL = "https://script.google.com/macros/s/AKfycbyUOv81iUNjSstcUiJDafcBTp9OtNs0YhcPxumOT_WaGnj70H7g_opkcZogunna2jM/exec";

  const handleApplyToken = () => {
    setAppsScriptUrl(PRESET_URL);
    localStorage.setItem(APPS_SCRIPT_URL_KEY, PRESET_URL);
  };

  const handleUrlChange = (val) => {
    setAppsScriptUrl(val);
    localStorage.setItem(APPS_SCRIPT_URL_KEY, val);
  };

  const lastSyncLabel = lastSyncAt ? new Date(lastSyncAt).toLocaleString() : 'Not synced yet';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-800 flex justify-between items-center bg-gray-900/50">
          <h2 className="text-xl font-bold text-white flex items-center gap-2"><SafeIcon icon={FiLayout} className="text-indigo-400" /> Settings</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><SafeIcon icon={FiX} className="text-xl" /></button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          <div className="space-y-4">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest block mb-2">Google Sheets Sync</label>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div className="rounded-lg border border-gray-800 bg-gray-950/50 p-3">
                <div className="text-gray-500 uppercase font-bold tracking-wider flex items-center gap-1.5">
                  <SafeIcon icon={isOnline ? FiWifi : FiWifiOff} /> Status
                </div>
                <div className="mt-1 text-gray-200 capitalize">{isOnline ? syncStatus : 'offline'}</div>
              </div>
              <div className="rounded-lg border border-gray-800 bg-gray-950/50 p-3">
                <div className="text-gray-500 uppercase font-bold tracking-wider">Pending</div>
                <div className="mt-1 text-gray-200">{dirtyCount} change{dirtyCount === 1 ? '' : 's'}</div>
              </div>
            </div>
            <p className="text-[10px] text-gray-500">
              Last synced: {lastSyncLabel}{retryCount ? ` · retry attempt ${retryCount}` : ''}
            </p>
            {blockedCount > 0 && (
              <div className="rounded-lg border border-red-500/25 bg-red-500/5 px-3 py-2 text-[10px] leading-relaxed text-red-300">
                {blockedCount} snippet{blockedCount === 1 ? '' : 's'} exceed the 48,000-character Google Sheets cloud-safe limit and are stored locally only until reduced.
              </div>
            )}
            {lastSyncError && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-[10px] leading-relaxed text-amber-300">
                Last sync issue: {lastSyncError}. Your local changes remain saved and will retry automatically.
              </div>
            )}

            <div className="relative">
              <input type="text" placeholder="Paste Apps Script Web App URL..." className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all pr-10" value={appsScriptUrl} onChange={(e) => handleUrlChange(e.target.value)} />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"><SafeIcon icon={FiLink} className="text-xs" /></div>
            </div>

            <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-lg p-3">
              <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider mb-2">Quick Sync Token</p>
              <button onClick={handleApplyToken} className="group w-full flex items-center justify-between gap-3 bg-gray-800/50 hover:bg-indigo-600/20 border border-gray-700 hover:border-indigo-500/50 text-left p-2 rounded-md transition-all">
                <div className="flex-1 overflow-hidden"><p className="text-[10px] text-gray-400 font-mono truncate">{PRESET_URL}</p></div>
                <div className="shrink-0 flex items-center gap-1.5 bg-indigo-600/20 group-hover:bg-indigo-600 px-2 py-1 rounded text-[9px] font-bold text-indigo-400 group-hover:text-white transition-colors"><SafeIcon icon={FiZap} /> AUTO-FILL</div>
              </button>
            </div>

            <button onClick={() => onSyncNow({ mode: 'manual' })} disabled={!isOnline} className="w-full flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-200 py-2 rounded-lg border border-gray-700 transition-all text-sm font-medium">
              <SafeIcon icon={FiRefreshCcw} className={syncStatus === 'syncing' ? 'animate-spin' : ''} />
              {!isOnline ? 'Offline' : syncStatus === 'syncing' ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>

          <hr className="border-gray-800" />

          <div className="space-y-3">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest flex items-center gap-2"><SafeIcon icon={FiClipboard} /> Clipboard Capture</label>
            <Toggle
              label="Automatic capture"
              help="Check clipboard when returning to the app."
              checked={Boolean(config.autoClipboardCapture)}
              onChange={(checked) => setConfig({ ...config, autoClipboardCapture: checked })}
            />
            <div>
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-gray-500">Desktop capture mode</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ['banner', 'Ask before save'],
                  ['auto_with_undo', 'Auto-save + Undo']
                ].map(([value, label]) => (
                  <button key={value} onClick={() => setConfig({ ...config, captureMode: value })} className={`rounded-lg border px-3 py-2 text-xs font-semibold transition-all ${config.captureMode === value ? 'border-indigo-500 bg-indigo-600/20 text-indigo-300' : 'border-gray-700 bg-gray-800 text-gray-400'}`}>{label}</button>
                ))}
              </div>
            </div>
          </div>

          <hr className="border-gray-800" />

          <div className="space-y-3">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest flex items-center gap-2"><SafeIcon icon={theme === 'dark' ? FiMoon : FiSun} /> Appearance</label>
            <button
              onClick={onToggleTheme}
              className="w-full flex items-center justify-between gap-4 rounded-xl border border-gray-800 bg-gray-950/40 p-3 text-left"
            >
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-200">
                  <SafeIcon icon={theme === 'dark' ? FiMoon : FiSun} />
                  {theme === 'dark' ? 'Dark mode' : 'Light mode'}
                </div>
                <div className="text-[10px] text-gray-500">Switch instantly. Your preference is remembered on this device.</div>
              </div>
              <span className="rounded-lg bg-indigo-600 px-3 py-1.5 text-[10px] font-bold text-white">
                {theme === 'dark' ? 'Use Light' : 'Use Dark'}
              </span>
            </button>
          </div>

          <hr className="border-gray-800" />

          <div className="space-y-3">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest flex items-center gap-2"><SafeIcon icon={FiType} /> Productivity</label>
            <Toggle
              label="Remember template values"
              help="Prefill variable values used previously on this device."
              checked={Boolean(config.rememberVariableValues)}
              onChange={(checked) => setConfig({ ...config, rememberVariableValues: checked })}
            />
            {config.rememberVariableValues && (
              <button onClick={() => localStorage.removeItem(TOKEN_VALUES_KEY)} className="w-full rounded-lg border border-gray-800 bg-gray-950/40 px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:text-gray-300">
                Clear remembered variable values
              </button>
            )}
            <Toggle
              label="Keyboard shortcuts"
              help="/ search · N new · 1–5 pinned copy · Shift+S sync"
              checked={Boolean(config.keyboardShortcuts)}
              onChange={(checked) => setConfig({ ...config, keyboardShortcuts: checked })}
              icon={FiCommand}
            />
          </div>

          <hr className="border-gray-800" />

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Library Tools</label>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={onInsights} className="flex items-center justify-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-xs font-bold text-gray-200 hover:bg-gray-700"><SafeIcon icon={FiBarChart2} /> Insights</button>
              <button onClick={onDuplicates} className="flex items-center justify-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-xs font-bold text-gray-200 hover:bg-gray-700"><SafeIcon icon={FiLayers} /> Duplicates</button>
              <button onClick={onTrash} className="col-span-2 flex items-center justify-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-xs font-bold text-gray-200 hover:bg-gray-700"><SafeIcon icon={FiTrash2} /> Trash & Recovery</button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Import / Export</label>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={onExport} className="flex items-center justify-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-xs font-bold text-gray-200 hover:bg-gray-700"><SafeIcon icon={FiDownload} /> Export</button>
              <button onClick={onImport} className="flex items-center justify-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-xs font-bold text-gray-200 hover:bg-gray-700"><SafeIcon icon={FiUpload} /> Import</button>
            </div>
          </div>

        </div>

        <div className="p-6 pt-0">
          <button onClick={onClose} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-lg font-medium transition-all shadow-lg">Done</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Toggle({ label, help, checked, onChange, icon = null }) {
  return (
    <label className="flex items-center justify-between gap-4 rounded-xl border border-gray-800 bg-gray-950/40 p-3">
      <div>
        <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-200">{icon && <SafeIcon icon={icon} />}{label}</div>
        <div className="text-[10px] text-gray-500">{help}</div>
      </div>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5 rounded border-gray-600 bg-gray-800 text-indigo-600" />
    </label>
  );
}